import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Coins, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export const fmtCoins = (minor) =>
  `${(Number(minor || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} COINS`;

export function PurchaseModal({ product, balanceMinor, onClose, onPurchased }) {
  // product: { id, name, product_type, price_minor, term_label }
  const [ackNoRefund, setAckNoRefund] = useState(false);
  const [ackRisk, setAckRisk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const idemKey = useRef(`ui:${product.id}:${Date.now()}`); // one key per modal open — double-click safe

  const price = Number(product.price_minor || 0);
  const sufficient = balanceMinor >= price;
  const after = balanceMinor - price;

  const confirm = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/member/shop/purchase", {
        product_type: product.product_type,
        product_id: product.id,
        idempotency_key: idemKey.current,
      });
      toast.success(`${product.name} purchased successfully`);
      window.dispatchEvent(new Event("pm-coin-balance-refresh"));
      onPurchased();
      onClose();
    } catch (err) {
      setError(formatApiError(err));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="purchase-modal">
      <div className="absolute inset-0 bg-black/75" onClick={() => !busy && onClose()} />
      <div className="card-lux relative w-full max-w-md p-6 sm:p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="eyebrow-label mb-1">Confirm Purchase</p>
            <h3 className="font-heading text-lg font-semibold text-white" data-testid="purchase-product-name">{product.name}</h3>
            <p className="text-[10px] font-mono text-[#64748B] mt-1 uppercase tracking-widest">{product.term_label}</p>
          </div>
          <button onClick={onClose} disabled={busy} className="text-[#64748B] hover:text-white transition-colors" data-testid="purchase-close-button">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border border-[#222530] rounded-lg divide-y divide-[#222530] text-sm mb-5">
          <div className="flex justify-between px-4 py-3">
            <span className="text-[#94A3B8]">Price</span>
            <span className="font-mono text-[#D4AF37]" data-testid="purchase-price">{fmtCoins(price)}</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-[#94A3B8]">Current balance</span>
            <span className="font-mono text-white" data-testid="purchase-balance-current">{fmtCoins(balanceMinor)}</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-[#94A3B8]">Balance after purchase</span>
            <span className={`font-mono ${sufficient ? "text-emerald-400" : "text-red-400"}`} data-testid="purchase-balance-after">
              {fmtCoins(Math.max(after, 0))}
            </span>
          </div>
        </div>

        {!sufficient && (
          <div className="mb-5 border border-red-400/25 bg-red-400/5 rounded-lg px-4 py-3" data-testid="purchase-insufficient-warning">
            <p className="text-xs text-red-400">Insufficient COINS balance for this purchase.</p>
            <Link to="/member/coins" className="text-xs text-[#D4AF37] underline underline-offset-4 hover:text-[#E5C158]" data-testid="purchase-topup-link">
              Top up Coins (1 USD = 1 COINS)
            </Link>
          </div>
        )}

        <label className="flex items-start gap-2.5 mb-3 cursor-pointer" data-testid="purchase-norefund-label">
          <input type="checkbox" checked={ackNoRefund} onChange={(e) => setAckNoRefund(e.target.checked)} className="mt-0.5 accent-[#D4AF37]" data-testid="purchase-norefund-checkbox" />
          <span className="text-[11px] text-[#94A3B8] leading-relaxed">
            I understand that all purchases are final and <span className="text-red-400">non-refundable</span>.
          </span>
        </label>
        <label className="flex items-start gap-2.5 mb-5 cursor-pointer" data-testid="purchase-risk-label">
          <input type="checkbox" checked={ackRisk} onChange={(e) => setAckRisk(e.target.checked)} className="mt-0.5 accent-[#D4AF37]" data-testid="purchase-risk-checkbox" />
          <span className="text-[11px] text-[#94A3B8] leading-relaxed">
            I acknowledge that trading foreign exchange and CFDs carries a high level of risk and may not be suitable for all investors. Past performance is not indicative of future results.
          </span>
        </label>

        {error && (
          <p className="mb-4 text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3 flex items-center gap-2" data-testid="purchase-error">
            <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
          </p>
        )}

        <button
          onClick={confirm}
          disabled={busy || !ackNoRefund || !ackRisk || !sufficient}
          className="btn-gold w-full py-3 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="purchase-confirm-button"
        >
          <Coins className="h-4 w-4" />
          {busy ? "Processing..." : sufficient ? `Confirm Purchase — ${fmtCoins(price)}` : "Insufficient Balance"}
        </button>
      </div>
    </div>
  );
}
